import { styled } from '@linaria/react';
import theme from '../../theme';

const { pageHeaderHeight, adminContentPadding } = theme.constants;

// ==================== State Components (Loading/Error/NotFound) ====================

export const LoadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 50vh;
    text-align: center;

    h2 {
        color: #dc3545;
        margin-bottom: 1rem;
    }

    p {
        color: #6c757d;
        margin-bottom: 1.5rem;
        font-size: 1.1rem;
    }
`;

export const LoadingSpinner = styled.div`
    width: 40px;
    height: 40px;
    border: 4px solid #e3e3e3;
    border-top: 4px solid #6c757d;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;

    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }
`;

export const ErrorContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 50vh;
    text-align: center;

    h2 {
        color: #dc3545;
        margin-bottom: 1rem;
    }

    p {
        color: #6c757d;
        margin-bottom: 1.5rem;
        font-size: 1.1rem;
    }
`;

export const ErrorActions = styled.div`
    display: flex;
    gap: 1rem;
`;

// Using global TextButton for consistent styling
export { TextButton as RetryButton } from '../../styles/button.style';
export { TextButton as BackButton } from '../../styles/button.style';

export const NotFoundContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 50vh;
    text-align: center;

    h2 {
        color: #dc3545;
        margin-bottom: 1rem;
    }

    p {
        color: #6c757d;
        margin-bottom: 1.5rem;
        font-size: 1.1rem;
    }
`;

// ==================== Layout Components ====================

export const DefaultProductLabel = styled.span`
    /* Inherits styling from parent, can add specific styles if needed */
`;

export const ProductPageContainer = styled.div`
    display: flex;
    flex-direction: column;
    height: calc(100vh - ${pageHeaderHeight} - ${adminContentPadding} - ${adminContentPadding});
    overflow: hidden;
    background-color: #f8f9fa;

    @media (max-width: 768px) {
        padding: 0.25rem;
        height: auto;
        min-height: calc(100vh - ${pageHeaderHeight});
        overflow: visible;
    }

    @media print {
        height: 100%;
        overflow: visible;
    }
`;

export const ProductContent = styled.div`
    /* Container for tab menu and product layout */
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;

    @media (max-width: 768px) {
        overflow: visible;
        flex: none;
        min-height: auto;
    }

    @media print {
        overflow: visible;
        height: auto;
        min-height: auto;
    }
`;

export const ProductLayout = styled.div`
    display: grid;
    grid-template-columns: 300px calc(100% - 300px);
    gap: 0.5rem;
    align-items: start;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    margin-top: 0.25rem;

    /* Custom scrollbar styling */
    &::-webkit-scrollbar {
        width: 8px;
    }

    &::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background: #c1c1c1;
        border-radius: 4px;

        &:hover {
            background: #a1a1a1;
        }
    }

    @media (max-width: 1024px) {
        grid-template-columns: 1fr;
        gap: 1rem;
    }

    @media (max-width: 768px) and (pointer: coarse) {
        display: flex;
        flex-direction: column;
        overflow: visible;
        flex: none;
        height: auto;
        min-height: auto;
        height: calc(100vh - 300px);
        overflow-y: auto;
    }

    @media print {
        display: block;
        grid-template-columns: 1fr;
        overflow: visible;
        height: auto;
        min-height: auto;
    }
`;

export const Sidebar = styled.div`
    @media (max-width: 1024px) {
        order: 2;
    }

    @media (max-width: 768px) and (pointer: coarse) {
        display: none;
    }

    @media print {
        display: none;
    }
`;

export const MainContent = styled.div`
    @media (max-width: 1024px) {
        order: 1;
    }

    @media (max-width: 768px) {
        order: 1;
        width: calc(100% - 0.25rem);
    }
`;

export const ProductSections = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2rem;
   
    @media (max-width: 1024px) {
        height: auto;
    }

    @media print {
        /* Force ALL sections visible regardless of active tab */
        > * {
            display: block !important;
        }
             height: 100%;
             overflow: visible;
    }
`;

// ==================== Split View Layout ====================

export const SplitViewContainer = styled.div`
    display: grid;
    grid-template-columns: 350px 1fr;
    gap: 1.5rem;
    flex: 1;
    min-height: 0;
    overflow: hidden;

    @media (max-width: 1440px) {
        /* Not enough room for split view - fall back to single column */
        grid-template-columns: 1fr;

        > *:first-child {
            display: none;
        }
    }

    @media print {
        display: block;
        grid-template-columns: 1fr;
        overflow: visible;
        height: auto;
    }
`;

export const SplitViewProductListPanel = styled.div`
    border-right: 1px solid #dee2e6;
    background: white;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    border-radius: 4px;
    border: 1px solid #dee2e6;

    @media print {
        display: none;
    }
`;

export const SplitViewCloseButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.2rem 0.4rem;
    color: #6c757d;
    font-size: 0.8rem;
    border-radius: 3px;
    transition: all 0.15s ease;
    display: flex;
    align-items: center;

    &:hover {
        color: #dc3545;
        background: rgba(0, 0, 0, 0.06);
    }
`;

export const SplitViewTabBar = styled.div`
    display: flex;
    align-items: center;
    gap: 0;
    border-bottom: 2px solid #dee2e6;
    background: #e9ecef;
    flex-shrink: 0;
`;

export const SplitViewTab = styled.button<{ $active: boolean }>`
    padding: 8px 20px;
    font-size: 0.82rem;
    font-weight: ${(props) => (props.$active ? '600' : '400')};
    color: ${(props) => (props.$active ? '#0066cc' : '#6c757d')};
    background: none;
    border: none;
    border-bottom: 2px solid ${(props) => (props.$active ? '#0066cc' : 'transparent')};
    margin-bottom: -2px;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 0.35rem;

    &:hover {
        color: #0066cc;
    }

    svg {
        font-size: 0.75rem;
    }
`;

export const SplitViewTabBarRight = styled.div`
    display: flex;
    align-items: center;
    gap: 0.25rem;
    margin-left: auto;
    padding-right: 0.5rem;
`;

export const SplitViewFilterButton = styled.button<{ $active?: boolean }>`
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.3rem 0.45rem;
    color: ${(props) => (props.$active ? '#0066cc' : '#6c757d')};
    font-size: 0.8rem;
    border-radius: 3px;
    transition: all 0.15s ease;
    display: flex;
    align-items: center;

    &:hover {
        color: #0066cc;
        background: rgba(0, 0, 0, 0.06);
    }
`;

export const SplitViewFilterPanel = styled.div`
    background: #fff;
    border-bottom: 1px solid #dee2e6;
    padding: 0.75rem;
    display: flex;
    gap: 1rem;
    flex-shrink: 0;
`;

export const SplitViewFilterSection = styled.div`
    flex: 1;
    min-width: 0;
`;

export const SplitViewFilterSectionTitle = styled.div`
    font-weight: 600;
    font-size: 0.8rem;
    margin-bottom: 0.4rem;
    color: #333;
`;

export const SplitViewFilterCheckboxGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.35rem;

    label {
        display: flex;
        align-items: center;
        gap: 0.4rem;
        font-size: 0.78rem;
        cursor: pointer;
        color: #495057;
        white-space: nowrap;

        &:hover {
            color: #007bff;
        }

        input[type='checkbox'] {
            cursor: pointer;
        }
    }
`;

export const SplitViewSearchContainer = styled.div`
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid #dee2e6;
    background: #fff;
    position: sticky;
    top: 0;
    z-index: 1;
    flex-shrink: 0;
`;

export const SplitViewSearchInput = styled.input`
    width: 100%;
    padding: 0.35rem 0.5rem;
    border: 1px solid #ced4da;
    border-radius: 4px;
    font-size: 0.8rem;
    outline: none;
    box-sizing: border-box;
    transition: border-color 0.15s ease;

    &:focus {
        border-color: #007bff;
        box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.15);
    }

    &::placeholder {
        color: #adb5bd;
    }
`;

export const SplitViewMainContent = styled.div`
    overflow-y: auto;
    display: flex;
    flex-direction: column;

    /* Custom scrollbar */
    &::-webkit-scrollbar {
        width: 8px;
    }
    &::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 4px;
    }
    &::-webkit-scrollbar-thumb {
        background: #c1c1c1;
        border-radius: 4px;
        &:hover {
            background: #a1a1a1;
        }
    }

    @media print {
        overflow: visible;
        height: auto;
    }
`;

export const SplitViewContentLoading = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 40vh;
    text-align: center;
    color: #6c757d;
    font-size: 0.95rem;

    p {
        margin-top: 0.75rem;
    }
`;

// ==================== Split View Search Settings ====================

// ==================== External IDs Section ====================

export const ExternalReferencesCard = styled.div`
    background: white;
    border-radius: 4px;
    padding: 2rem;
    border: 1px solid #e9ecef;

    @media (max-width: 768px) {
        padding: 0.5rem;
    }
`;

export const ExternalReferencesTitle = styled.h2`
    color: #333;
    margin: 0 0 1.5rem 0;
    font-size: 1.5rem;
    font-weight: 700;
    border-bottom: 3px solid #6c757d;
    padding-bottom: 0.5rem;
`;

export const ExternalIdsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`;

export const ExternalIdItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.25rem 0.5rem;
    background: #f8f9fa;
    border-radius: 4px;
    border-left: 4px solid #dee2e6;

    label {
        font-weight: 600;
        color: #495057;
        margin-right: 1rem;
        min-width: 120px;
    }

    span {
        color: #333;
        text-align: right;
        flex: 1;
        word-break: break-word;
    }
`;
